import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import LocalStorage from 'utils/bom-dom-manipulation/local-storage';
import {
  MILLISECONDS_IN_SECOND,
  SECONDS_PER_MINUTE
} from 'utils/constants/dateInfo/time-numbers';
import SpringConfigs from 'utils/constants/swarm/spring-configs';
import { CommandNames } from 'utils/constants/swarm/swarm-command-names';
import { GLOBALS } from 'utils/generic/global-variables';
import { storageKeyName } from 'utils/generic/storage-key-name';
import { isMobile } from 'utils/is-mobile';
import { swarmCommand } from 'services/get-swarm-data';
import { useAccountLogout } from 'hooks/auth/useAccountLogout';
import { setGeoLocationShakeData } from 'store/actions/app-data';
import {
  resetGpsTrackingParams,
  updateGpsTrackingParams
} from 'store/actions/user-data';
import { getGeolocationShakeData } from 'store/selectors/app-data';
import { getIsConnected } from 'store/selectors/socket';
import { getGpsTrackingParams } from 'store/selectors/user-data';

export const useGpsTracking = (): {
  popupType: string | undefined;
  checkLocation: typeof checkLocation;
  gpsLogout: typeof gpsLogout;
} => {
  const dispatch = useDispatch();
  const accountLogout = useAccountLogout();
  const gpsTrackingParams = useSelector(getGpsTrackingParams);
  const geolocationShakeData = useSelector(getGeolocationShakeData);
  const socetReConnect = useSelector(getIsConnected);

  const [needCheckGeoLocation, setNeedCheckGeoLocation] = useState(true);

  const sendGetGeoToken = useMemo(() => {
    return gpsTrackingParams.sendGeoToken;
  }, [gpsTrackingParams.sendGeoToken]);

  const sendGetGeoTokenAction = useMemo(() => {
    return gpsTrackingParams.action;
  }, [gpsTrackingParams.action]);

  useEffect(() => {
    socetReConnect && checkLocation();
  }, [socetReConnect]);

  useEffect(() => {
    if (sendGetGeoTokenAction) {
      checkLocation();
      clearCheckInterval();
    } else if (gpsTrackingParams.popupType) {
      clearCheckInterval();
    } else if (needCheckGeoLocation) {
      setCheckInterval();
    }
  }, [sendGetGeoTokenAction, gpsTrackingParams.popupType]);

  const clearCheckInterval = useCallback(() => {
    if (gpsTrackingParams.checkInterval) {
      clearInterval(gpsTrackingParams.checkInterval);

      updateGpsTrackingParams({
        checkInterval: 0
      });
    }
  }, [gpsTrackingParams.checkInterval]);

  const setCheckInterval = useCallback(() => {
    if (!gpsTrackingParams.checkInterval) {
      const interval = setInterval(() => {
        checkLocation();
      }, SpringConfigs.GPS_TRACKING_CHECKING_INTERVAL * SECONDS_PER_MINUTE * MILLISECONDS_IN_SECOND);

      dispatch(
        updateGpsTrackingParams({
          checkInterval: interval
        })
      );
    }
  }, [gpsTrackingParams.checkInterval]);

  useEffect(() => {
    if (geolocationShakeData) {
      if (GLOBALS.geolocationWindow) {
        GLOBALS.geolocationWindow.close();
        GLOBALS.geolocationWindow = null;
      }

      if (geolocationShakeData !== 'confirm_geo_token') {
        dispatch(
          updateGpsTrackingParams({
            popupType: 'shakeError'
          })
        );
      } else {
        gpsTrackingParams.validateGeoRestrictionSuccessCallback?.();

        dispatch(setGeoLocationShakeData(null));
        dispatch(resetGpsTrackingParams());
      }
    }
  }, [geolocationShakeData]);

  const checkLocation = useCallback(() => {
    navigator.geolocation.getCurrentPosition(
      onGeoLocationSuccess,
      onGeoLocationError,
      {
        enableHighAccuracy: true,
        timeout: 5000,
        maximumAge: 0
      }
    );
  }, [gpsTrackingParams]);

  const onGeoLocationError = useCallback<PositionErrorCallback>(() => {
    dispatch(
      updateGpsTrackingParams({
        popupType: 'error'
      })
    );
  }, []);

  const onGeoLocationSuccess = useCallback<PositionCallback>(
    pos => {
      const crd = pos.coords;
      sendValidateGeoCommand(crd.latitude, crd.longitude);
    },
    [gpsTrackingParams]
  );

  const sendValidateGeoCommand = useCallback(
    (lat: number, lng: number) => {
      const params = {
        lat: lat,
        lng: lng
      };

      if (
        !gpsTrackingParams.popupType &&
        !gpsTrackingParams.loadingValidateGeoRestriction
      ) {
        dispatch(
          updateGpsTrackingParams({
            loadingValidateGeoRestriction: true
          })
        );

        swarmCommand(
          CommandNames.VALIDATE_GEO_RESTRICTION,
          params,
          validateGeoRestrictionCallback,
          null,
          (data: any) => {
            checksErrorCallback(data);

            dispatch(
              updateGpsTrackingParams({
                loadingValidateGeoRestriction: false
              })
            );
          }
        );
      }
    },
    [gpsTrackingParams]
  );

  const gpsLogout = useCallback(() => {
    const authData = !!JSON.parse(
      LocalStorage.getItem(storageKeyName('account', 'AUTH_DATA'))
    );

    if (authData) {
      accountLogout();
    }
  }, []);

  const validateGeoRestrictionCallback = useCallback(
    (data: any) => {
      dispatch(
        updateGpsTrackingParams({
          loadingValidateGeoRestriction: false
        })
      );

      if (!data.result) {
        dispatch(
          updateGpsTrackingParams({
            popupType: 'error'
          })
        );
      } else {
        if (sendGetGeoToken && sendGetGeoTokenAction) {
          sendGetGeoTokenCommand();
        }
      }
    },
    [gpsTrackingParams]
  );

  const sendGetGeoTokenCommand = useCallback(() => {
    if (!gpsTrackingParams.loadingGetGeoToken) {
      dispatch(
        updateGpsTrackingParams({
          loadingGetGeoToken: true
        })
      );

      swarmCommand(
        CommandNames.GET_GEO_TOKEN,
        {
          action: sendGetGeoTokenAction
        },
        getGeoTokenCallback,
        null,
        (data: null) => {
          checksErrorCallback(data);

          dispatch(
            updateGpsTrackingParams({
              loadingGetGeoToken: false
            })
          );
        }
      );
    }
  }, [gpsTrackingParams]);

  const getGeoTokenCallback = useCallback(
    (data: any) => {
      dispatch(
        updateGpsTrackingParams({
          loadingGetGeoToken: false
        })
      );

      if (data.result === 0) {
        const gpsTrackingLink = `${SpringConfigs.GPS_TRACKING_URL}/?token=${data.details.token}&language=eng&geoDataWriteInterval=50&geoDataListenDuration=20000&siteId=${SpringConfigs.PARTNER_ID}&websocketUrl=${SpringConfigs.SWARM_URL}`;

        if (!isMobile()) {
          dispatch(
            updateGpsTrackingParams({
              popupType: 'qr-popup',
              trackingLink: gpsTrackingLink,
              trackingDuration: data.details.duration
            })
          );
        } else {
          dispatch(
            updateGpsTrackingParams({
              popupType: 'confirm',
              trackingLink: gpsTrackingLink
            })
          );
        }
      } else {
        checksErrorCallback(data);
      }
    },
    [gpsTrackingParams]
  );

  const checksErrorCallback = useCallback(
    // @Todo FIX_MY_TYPE @todo-account
    (errorData: Record<string, any> | null = null) => {
      gpsLogout();
      gpsTrackingParams.validateGeoRestrictionCancelCallback?.();

      if (errorData) {
        setNeedCheckGeoLocation(false);
        dispatch(
          updateGpsTrackingParams({
            popupType: 'error',
            errorPopupTitle: errorData.msg,
            errorPopupMsg: errorData.data
          })
        );
      } else {
        setCheckInterval();
        dispatch(resetGpsTrackingParams());
      }
    },
    [gpsTrackingParams]
  );

  return {
    popupType: gpsTrackingParams.popupType,
    checkLocation: checkLocation,
    gpsLogout: gpsLogout
  };
};
